import React, { useState, useEffect, ChangeEvent } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Card, Form, Select, Input, DatePicker, Button, message, Spin, Row, Col, Table, Checkbox, Tag, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, WarningOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { PageTitle, StandardCard, PageWrapper } from '../common';
import { theme } from '../../theme';

const { Option } = Select;

interface Producto {
  id: number;
  descripcion: string;
}

interface Provincia {
  id: number;
  provincia: string;
}

interface Localidad {
  id: number;
  localidad: string;
  provincia: number;
}

interface Padron {
  id: number;
  denominacion: string;
  nc: string;
}

interface Sucursal {
  id: number;
  denominacion: string;
}

const AltaSinContratos = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // Estados del formulario
  const [tipoOrden, setTipoOrden] = useState('');
  const [producto, setProducto] = useState('');
  const [destino, setDestino] = useState('');
  const [cliente, setCliente] = useState('');
  const [clienteOtro, setClienteOtro] = useState('');
  const [destinatario, setDestinatario] = useState('');
  const [sucursalOtra, setSucursalOtra] = useState('');
  const [motivo, setMotivo] = useState(0);
  const [descripcionMotivo, setDescripcionMotivo] = useState('');
  const [fecha, setFecha] = useState<dayjs.Dayjs | null>(dayjs());
  const [empresa, setEmpresa] = useState('');
  const [direccion, setDireccion] = useState('');
  const [provincia, setProvincia] = useState('');
  const [localidad, setLocalidad] = useState('');

  // Estados para los datos de los selects
  const [productos, setProductos] = useState<Producto[]>([]);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [destinos, setDestinos] = useState<Padron[]>([]);
  const [clientes, setClientes] = useState<Padron[]>([]);
  const [destinatarios, setDestinatarios] = useState<Padron[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);

  // Estados de control
  const [cargando, setCargando] = useState(false);
  const [grabarCargando, setGrabarCargando] = useState(false);

  // Estados y handlers para Selección, Control y Resumen
  const [origen, setOrigen] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [aplicadas, setAplicadas] = useState<any[]>([]);
  const [resumen, setResumen] = useState<any>(null);
  const [fardosProcesados, setFardosProcesados] = useState<Set<number>>(new Set());
  const [mensajeControl, setMensajeControl] = useState('');
  const [tipoMensajeControl, setTipoMensajeControl] = useState<'success' | 'error' | ''>('');
  const [cargandoControl, setCargandoControl] = useState(false);
  const [checkboxStates, setCheckboxStates] = useState<Map<number, boolean>>(new Map());

  // Estados para búsqueda del padrón
  const [searchOrigen, setSearchOrigen] = useState('');
  const [showSearchOrigen, setShowSearchOrigen] = useState(false);
  const [selectedOrigen, setSelectedOrigen] = useState<Padron | null>(null);
  
  const [searchClienteSeleccion, setSearchClienteSeleccion] = useState('');
  const [showSearchClienteSeleccion, setShowSearchClienteSeleccion] = useState(false);
  const [selectedClienteSeleccion, setSelectedClienteSeleccion] = useState<Padron | null>(null);

  // Estados para búsqueda avanzada en Cliente (Cambio de Dueño) y Destino (Retiro Cliente)
  const [searchClienteCambio, setSearchClienteCambio] = useState('');
  const [showSearchClienteCambio, setShowSearchClienteCambio] = useState(false);
  const [selectedClienteCambio, setSelectedClienteCambio] = useState<Padron | null>(null);

  const [searchClienteOtro, setSearchClienteOtro] = useState('');
  const [showSearchClienteOtro, setShowSearchClienteOtro] = useState(false);
  const [selectedClienteOtro, setSelectedClienteOtro] = useState<Padron | null>(null);

  const [searchDestinoRetiro, setSearchDestinoRetiro] = useState('');
  const [showSearchDestinoRetiro, setShowSearchDestinoRetiro] = useState(false);
  const [selectedDestinoRetiro, setSelectedDestinoRetiro] = useState<Padron | null>(null);

  // Estado para controlar si los campos principales están bloqueados después de la selección
  const [camposPrincipalesBloqueados, setCamposPrincipalesBloqueados] = useState(false);
  
  // Estado para controlar si los campos de Origen y Cliente están bloqueados para tipo B (Baja)
  const [origenBajaBloqueado, setOrigenBajaBloqueado] = useState(false);
  const [clienteBajaBloqueado, setClienteBajaBloqueado] = useState(false);

  // Opciones del tipo de orden
  const tiposOrden = [
    { value: 'A', label: 'A Otra Sucursal' },
    { value: 'D', label: 'Cambio de Dueño' },
    { value: 'E', label: 'Retiro Cliente' },
    { value: 'B', label: 'Baja' },
  ];

  // Opciones de motivo (solo para tipo B - Baja)
  const motivos = [
    { value: 0, label: 'Seleccione un motivo' },
    { value: 1, label: 'Perdida' },
    { value: 2, label: 'Robo' },
    { value: 3, label: 'Daño' },
    { value: 4, label: 'Incendio' },
    { value: 5, label: 'Otros' },
  ];

  // Estados para fardos seleccionados
  const [fardosSeleccionados, setFardosSeleccionados] = useState<any[]>([]);
  const [fardosAcumulados, setFardosAcumulados] = useState<any[]>([]);

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setCargando(true);
        const token = await getAccessTokenSilently();
        
        const idEmpresa = user?.id_empresa;
        if (!idEmpresa) {
          message.error('No se encontró el ID de empresa en el usuario');
          return;
        }

        // Cargar datos del padrón
        const padronUrl = `${process.env.REACT_APP_PADRON_URL}/leer_padron/${idEmpresa}/`;
        const padronRes = await fetch(padronUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!padronRes.ok) {
          throw new Error(`Error al cargar padrón: ${padronRes.status}`);
        }

        const padronData = await padronRes.json();
        setDestinos(padronData);
        setClientes(padronData);
        setDestinatarios(padronData);

        // Cargar provincias
        const provinciasUrl = `${process.env.REACT_APP_COMMON_URL}/listar_provincias/`;
        const provinciasRes = await fetch(provinciasUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const provinciasData = await provinciasRes.json();
        setProvincias(provinciasData);

        // Cargar productos (solo tipo = 2)
        const productosUrl = `${process.env.REACT_APP_BALANZA_URL}/productos/tipo/2/`;
        const productosRes = await fetch(productosUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const productosData = await productosRes.json();
        setProductos(productosData);

        // Cargar sucursales
        const sucursalesUrl = `${process.env.REACT_APP_SUCURSAL_URL}/sucursales/${idEmpresa}/`;
        const sucursalesRes = await fetch(sucursalesUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const sucursalesData = await sucursalesRes.json();
        setSucursales(sucursalesData);

      } catch (error) {
        console.error('Error al cargar datos:', error);
        message.error('Error al cargar los datos. Por favor, intente nuevamente.');
      } finally {
        setCargando(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, user]);

  // Cargar localidades cuando cambia la provincia
  useEffect(() => {
    const fetchLocalidades = async () => {
      if (provincia) {
        try {
          const token = await getAccessTokenSilently();
          const url = `${process.env.REACT_APP_COMMON_URL}/listar_localidades/${provincia}/`;
          const res = await fetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const data = await res.json();
          setLocalidades(data);
        } catch (error) {
          console.error('Error al cargar localidades:', error);
          message.error('Error al cargar localidades');
        }
      } else {
        setLocalidades([]);
      }
    };

    fetchLocalidades();
  }, [provincia, getAccessTokenSilently]);

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-dropdown')) {
        setShowSearchOrigen(false);
        setShowSearchClienteSeleccion(false);
        setShowSearchClienteCambio(false);
        setShowSearchClienteOtro(false);
        setShowSearchDestinoRetiro(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handlers para cambios en el formulario
  const handleTipoOrdenChange = (value: string) => {
    setTipoOrden(value);
    // Limpiar campos específicos según el tipo
    if (value !== 'B') {
      setMotivo(0);
      setDescripcionMotivo('');
    }
    if (value !== 'A') {
      setSucursalOtra('');
    }
    if (value !== 'D' && value !== 'E') {
      setDestino('');
      setCliente('');
    }
    
            // Limpiar campos de selección cuando cambia el tipo de orden
        setOrigen('');
        setDesde('');
        setHasta('');
        setSelectedOrigen(null);
        setSelectedClienteSeleccion(null);
        setSelectedClienteCambio(null);
        setSelectedClienteOtro(null);
        setSearchOrigen('');
        setSearchClienteSeleccion('');
        setSearchClienteCambio('');
        setSearchClienteOtro('');
        setShowSearchOrigen(false);
        setShowSearchClienteSeleccion(false);
        setShowSearchClienteCambio(false);
        setShowSearchClienteOtro(false);
        setCamposPrincipalesBloqueados(false);
        setOrigenBajaBloqueado(false);
        setClienteBajaBloqueado(false);
    
    // Para tipo B (Baja), configurar automáticamente origen y cliente en Seleccionar
    if (value === 'B') {
      // Para baja, el origen y cliente deben ser configurados manualmente en la sección Seleccionar
      // No se bloquean automáticamente como en D y E
      setCamposPrincipalesBloqueados(false);
      setOrigenBajaBloqueado(false);
      setClienteBajaBloqueado(false);
    }
  };

  const handleProductoChange = (value: string) => {
    setProducto(value);
    console.log('Debug - Producto seleccionado en formulario principal:', value);
  };

  const handleDestinoChange = (value: string) => {
    setDestino(value);
    
    // Para tipo E, configurar automáticamente origen y cliente en Seleccionar
    if (tipoOrden === 'E') {
      if (value) {
        const destinoSeleccionado = destinos.find(dest => dest.id.toString() === value);
        if (destinoSeleccionado) {
          setOrigen(value);
          setSelectedOrigen(destinoSeleccionado);
          setSearchOrigen(`${destinoSeleccionado.nc} - ${destinoSeleccionado.denominacion}`);
          
          setSelectedClienteSeleccion(destinoSeleccionado);
          setSearchClienteSeleccion(`${destinoSeleccionado.nc} - ${destinoSeleccionado.denominacion}`);
          
          // Bloquear los campos principales después de la selección
          setCamposPrincipalesBloqueados(true);
        }
      } else {
        // Limpiar campos cuando se pasa una cadena vacía
        setOrigen('');
        setSelectedOrigen(null);
        setSearchOrigen('');
        setSelectedClienteSeleccion(null);
        setSearchClienteSeleccion('');
        
        // Desbloquear los campos principales cuando se limpia la selección
        setCamposPrincipalesBloqueados(false);
      }
    }
  };

  const handleClienteChange = (value: string) => {
    setCliente(value);
    
    // Para tipos D y E, configurar automáticamente origen y cliente en Seleccionar
    if (tipoOrden === 'D' || tipoOrden === 'E') {
      if (value) {
        const clienteSeleccionado = clientes.find(cli => cli.id.toString() === value);
        if (clienteSeleccionado) {
          setOrigen(value);
          setSelectedOrigen(clienteSeleccionado);
          setSearchOrigen(`${clienteSeleccionado.nc} - ${clienteSeleccionado.denominacion}`);
          
          setSelectedClienteSeleccion(clienteSeleccionado);
          setSearchClienteSeleccion(`${clienteSeleccionado.nc} - ${clienteSeleccionado.denominacion}`);
          
          // Bloquear los campos principales después de la selección
          setCamposPrincipalesBloqueados(true);
        }
      } else {
        // Limpiar campos cuando se pasa una cadena vacía
        setOrigen('');
        setSelectedOrigen(null);
        setSearchOrigen('');
        setSelectedClienteSeleccion(null);
        setSearchClienteSeleccion('');
        
        // Desbloquear los campos principales cuando se limpia la selección
        setCamposPrincipalesBloqueados(false);
      }
    }
  };

  const handleDestinatarioChange = (value: string) => {
    setDestinatario(value);
  };

  const handleSucursalOtraChange = (value: string) => {
    setSucursalOtra(value);
  };

  const handleMotivoChange = (value: number) => {
    setMotivo(value);
  };

  const handleDescripcionMotivoChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDescripcionMotivo(e.target.value);
  };

  const handleFechaChange = (date: dayjs.Dayjs | null) => {
    setFecha(date);
  };

  const handleEmpresaChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmpresa(e.target.value);
  };

  const handleDireccionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDireccion(e.target.value);
  };

  const handleProvinciaChange = (value: string) => {
    setProvincia(value);
    setLocalidad(''); // Limpiar localidad cuando cambia provincia
  };

  const handleLocalidadChange = (value: string) => {
    setLocalidad(value);
  };

  // Validar formulario antes de enviar
  const validarFormulario = () => {
    if (!tipoOrden) {
      message.error('Debe seleccionar un tipo de orden');
      return false;
    }
    if (!producto) {
      message.error('Debe seleccionar un producto');
      return false;
    }
    if (!fecha) {
      message.error('Debe seleccionar una fecha');
      return false;
    }

    // Validaciones específicas según el tipo de orden
    if (tipoOrden === 'A') {
      if (!sucursalOtra) {
        message.error('Debe seleccionar una sucursal');
        return false;
      }
    }

    if (tipoOrden === 'E') {
      if (!destino) {
        message.error('Debe seleccionar un destino');
        return false;
      }
    }

    if (tipoOrden === 'D') {
      if (!cliente) {
        message.error('Debe seleccionar un cliente');
        return false;
      }
      if (!selectedClienteOtro) {
        message.error('Debe seleccionar un cliente destino');
        return false;
      }
    }

    if (tipoOrden === 'E') {
      if (!empresa) {
        message.error('Debe ingresar una empresa');
        return false;
      }
      if (!direccion) {
        message.error('Debe ingresar una dirección');
        return false;
      }
      if (!provincia) {
        message.error('Debe seleccionar una provincia');
        return false;
      }
      if (!localidad) {
        message.error('Debe seleccionar una localidad');
        return false;
      }
    }

    if (tipoOrden === 'B') {
      if (!motivo || motivo === 0) {
        message.error('Debe seleccionar un motivo');
        return false;
      }
      if (!(descripcionMotivo || '').trim()) {
        message.error('Debe ingresar una descripción del motivo');
        return false;
      }
      // Validar que se haya seleccionado un cliente en la sección Seleccionar
      if (!selectedClienteSeleccion) {
        message.error('Debe seleccionar un cliente en la sección Seleccionar');
        return false;
      }
    }

    return true;
  };

  // Enviar formulario
  const handleEnviar = async () => {
    if (!validarFormulario()) {
      return;
    }

    // Validar que haya datos de resumen para grabar
    if (!resumen || fardosAcumulados.length === 0) {
      message.error('No hay datos de resumen para grabar. Ejecute el control y resuma primero.');
      return;
    }

    try {
      setGrabarCargando(true);
      const token = await getAccessTokenSilently();
      const idEmpresa = user?.id_empresa;
      const idSucursal = user?.id_sucursal;
      //const idSucursal=1;

      if (!idEmpresa) {
        message.error('No se encontró el ID de empresa');
        return;
      }

      if (!idSucursal) {
        message.error('No se encontró el ID de sucursal');
        return;
      }

      const ordenData = {
        tipo: tipoOrden,
        producto,
        fecha: fecha?.format('YYYY-MM-DD'),
        destino: (tipoOrden === 'D' || tipoOrden === 'E') ? (tipoOrden === 'D' ? cliente : destino) : null,
        cliente: tipoOrden === 'D' ? cliente : null,
        cliente_otro: tipoOrden === 'D' ? (selectedClienteOtro ? selectedClienteOtro.id : null) : null,
        origen_id: selectedOrigen ? selectedOrigen.id : (parseInt(origen) || 0),
        cliente_id:  selectedClienteSeleccion ? selectedClienteSeleccion.id : (parseInt(cliente) || 0),
        sucursal_otra: tipoOrden === 'A' ? sucursalOtra : null,
        motivo: tipoOrden === 'B' ? motivo : null,
        descripcion_motivo: tipoOrden === 'B' ? descripcionMotivo : '',
        empresa,
        direccion,
        provincia,
        localidad,
        fardos: fardosAcumulados // Incluir los fardos acumulados
      };

      const url = `${process.env.REACT_APP_ORDENES_URL}/alta_ordenes/${idEmpresa}/${idSucursal}/`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ordenData)
      });

      if (response.ok) {
        const result = await response.json();
        message.success('Orden creada exitosamente');
        
        // Limpiar formulario
        form.resetFields();
        setTipoOrden('');
        setProducto('');
        setDestino('');
        setCliente('');
        setClienteOtro('');
        setSucursalOtra('');
        setMotivo(0);
        setDescripcionMotivo('');
        // No limpiar la fecha, mantener la fecha actual
        // setFecha(null);
        setEmpresa('');
        setDireccion('');
        setProvincia('');
        setLocalidad('');
        
        // Limpiar datos de control y resumen
        setAplicadas([]);
        setFardosAcumulados([]);
        setResumen(null);
        setFardosProcesados(new Set());
        setCheckboxStates(new Map());
        setMensajeControl('');
        setTipoMensajeControl('');
        
        // Limpiar campos de selección
        setOrigen('');
        setDesde('');
        setHasta('');
        setSelectedOrigen(null);
        setSelectedClienteSeleccion(null);
        setSelectedClienteCambio(null);
        setSelectedClienteOtro(null);
        setSelectedDestinoRetiro(null);
        setSearchOrigen('');
        setSearchClienteSeleccion('');
        setSearchClienteCambio('');
        setSearchClienteOtro('');
        setSearchDestinoRetiro('');
        setShowSearchOrigen(false);
        setShowSearchClienteSeleccion(false);
        setShowSearchClienteCambio(false);
        setShowSearchClienteOtro(false);
        setShowSearchDestinoRetiro(false);
        setCamposPrincipalesBloqueados(false);
        setOrigenBajaBloqueado(false);
        setClienteBajaBloqueado(false);
      } else {
        const errorData = await response.json();
        message.error(`Error al crear la orden: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      message.error('Error al crear la orden. Por favor, intente nuevamente.');
    } finally {
      setGrabarCargando(false);
    }
  };

  const handleCancelar = () => {
    navigate('/ordenes');
  };

  // Estados y handlers para Selección, Control y Resumen
  const handleAplicacionChange = (id: number, aplicado: boolean) => {
    setCheckboxStates(prev => new Map(prev).set(id, aplicado));
  };
  const handleTildarTodos = () => {
    const newCheckboxStates = new Map();
    aplicadas.forEach(item => {
      newCheckboxStates.set(item.id, true);
    });
    setCheckboxStates(newCheckboxStates);
  };
  const handleDestildarTodos = () => {
    const newCheckboxStates = new Map();
    aplicadas.forEach(item => {
      newCheckboxStates.set(item.id, false);
    });
    setCheckboxStates(newCheckboxStates);
  };

  const handleLimpiarAcumulados = () => {
    setFardosAcumulados([]);
    setResumen(null);
    setMensajeControl('Fardos acumulados limpiados correctamente.');
    setTipoMensajeControl('success');
  };
  const handleControl = async (showMessages = true) => {
    try {
      if (showMessages) {
        setCargandoControl(true);
        setMensajeControl('');
      }

      // Validar campos requeridos según el tipo de orden
      console.log('Debug - Valores de campos:', {
        producto,
        cliente,
        destino,
        desde,
        hasta,
        tipoProducto: typeof producto,
        tipoCliente: typeof cliente,
        tipoDestino: typeof destino,
        tipoDesde: typeof desde,
        tipoHasta: typeof hasta
      });
      
      // Para tipo 'E', usar destino en lugar de cliente
      const clienteParaControl = (tipoOrden === 'E') ? destino : cliente;
      
      // Para tipo 'B', usar el cliente seleccionado en la sección Seleccionar
      const clienteParaControlFinal = (tipoOrden === 'B') ? (selectedClienteSeleccion ? selectedClienteSeleccion.id.toString() : '') : clienteParaControl;
      
      if (!producto || producto === '' ||  !desde || desde === '' || !hasta || hasta === '') {
        if (showMessages) {
          const campoCliente = (tipoOrden === 'E') ? 'destino' : (tipoOrden === 'B') ? 'cliente en Seleccionar' : 'cliente';
          setMensajeControl(`Por favor, complete los campos de producto, ${campoCliente}, desde y hasta para el control`);
          setTipoMensajeControl('error');
        }
        return;
      }

      // Validación específica para tipo 'B'
      if (tipoOrden === 'B' && !selectedClienteSeleccion) {
        if (showMessages) {
          setMensajeControl('Por favor, seleccione un cliente en la sección Seleccionar para el control');
          setTipoMensajeControl('error');
        }
        return;
      }

      const token = await getAccessTokenSilently();
      const idEmpresa = user?.id_empresa;

      if (!idEmpresa) {
        if (showMessages) {
          setMensajeControl('No se encontró el ID de empresa en el usuario');
          setTipoMensajeControl('error');
        }
        return;
      }

      // Construir URL para el control (órdenes sin contrato)
      // Para tipo 'E', usar destino en lugar de cliente
      // Para tipo 'B', usar el cliente seleccionado en la sección Seleccionar
      const clienteParaURL = (tipoOrden === 'E') ? destino : (tipoOrden === 'B') ? clienteParaControlFinal : cliente;
      const url = `${process.env.REACT_APP_ORDENES_URL}/control_fardos_contrato/${idEmpresa}/${0}/${producto}/${origen || '0'}/${clienteParaURL || '0'}/${desde}/${hasta}/`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error en el control: ${response.status}`);
      }

      const controlData = await response.json();
      console.log('Datos de control recibidos:', controlData);
      
      // Filtrar fardos que ya están en el resumen Y en fardos acumulados
      const fardosNuevos = controlData.filter((item: any) => {
        const nroFardo = item.nro_fardo || item.id;
        const yaProcesado = fardosProcesados.has(nroFardo);
        const yaAcumulado = fardosAcumulados.some(fardo => fardo.id === nroFardo);
        
        if (yaProcesado) {
          console.log(`Fardo ${nroFardo} ya está en el resumen, excluyendo del control`);
        }
        if (yaAcumulado) {
          console.log(`Fardo ${nroFardo} ya está en fardos acumulados, excluyendo del control`);
        }
        return !yaProcesado && !yaAcumulado;
      });
      
      console.log(`Fardos nuevos (no duplicados): ${fardosNuevos.length} de ${controlData.length}`);
      
      // Detectar fardos duplicados solo entre los nuevos
      const fardosCount = new Map();
      console.log('Analizando duplicados entre fardos nuevos en handleControl...');
      
      fardosNuevos.forEach((item: any, index: number) => {
        const nroFardo = item.nro_fardo || item.id;
        console.log(`Fardo ${index}: nro_fardo = ${nroFardo}, tipo = ${typeof nroFardo}`);
        
        if (nroFardo !== undefined && nroFardo !== null) {
          const currentCount = fardosCount.get(nroFardo) || 0;
          fardosCount.set(nroFardo, currentCount + 1);
          console.log(`Contador para fardo ${nroFardo}: ${currentCount + 1}`);
        }
      });
      
      console.log('Mapa de conteo de fardos nuevos en handleControl:', Object.fromEntries(fardosCount));
      
      // Formatear los datos para el sector Control (solo fardos nuevos)
      const datosControl = fardosNuevos.map((item: any, index: number) => {
        const nroFardo = item.nro_fardo  || item.id;
        const esDuplicado = fardosCount.get(nroFardo) > 1;
        // Normalizar el valor de aplicacion
        const aplicadoBackend = (typeof item.aplicacion === 'string' && (item.aplicacion || '').trim().toUpperCase() === 'SI');
        return {
          id:item.id_fardo,
          nroFardo:item.nro_fardo,
          descripcion: `${item.descripcion || `Fardo ${nroFardo}`}${esDuplicado ? ' (DUPLICADO)' : ''}`,
          cantidad: item.cantidad || 0,
          fecha: item.fecha || new Date().toISOString().split('T')[0],
          aplicado: esDuplicado ? false : aplicadoBackend,
          stock: item.stock === 'SI' || item.stock === true ? 1 : 0,
          orden: item.orden || 'NO',
          alta: esDuplicado ? false : aplicadoBackend
        };
      });
      
      console.log('Datos control finales en handleControl:', datosControl);
      setAplicadas(datosControl);
      // Inicializar los checkboxes: true solo si stock === 1
      const initialCheckboxStates = new Map();
      datosControl.forEach((item: any) => {
        initialCheckboxStates.set(item.id, item.stock === 1);
      });
      setCheckboxStates(initialCheckboxStates);
      
      if (showMessages) {
        // Contar duplicados para el mensaje
        const duplicados = Array.from(fardosCount.values()).filter(count => count > 1).length;
        const excluidos = controlData.length - fardosNuevos.length;
        console.log(`Total de fardos duplicados detectados en handleControl: ${duplicados}`);
        console.log(`Fardos excluidos por ya estar en resumen: ${excluidos}`);
        
        if (duplicados > 0 || excluidos > 0) {
          let mensajeFinal = 'Control realizado correctamente.';
          if (excluidos > 0) {
            mensajeFinal += ` Se excluyeron ${excluidos} fardos que ya están en el resumen.`;
          }
          if (duplicados > 0) {
            mensajeFinal += ` Se detectaron ${duplicados} fardos duplicados que fueron marcados como no aplicados.`;
          }
          setMensajeControl(mensajeFinal);
        } else {
          setMensajeControl('Control realizado correctamente');
        }
        setTipoMensajeControl('success');
      }

    } catch (error) {
      console.error('Error en el control:', error);
      if (showMessages) {
        setMensajeControl('Error al realizar el control. Por favor, intente nuevamente.');
        setTipoMensajeControl('error');
      }
    } finally {
      if (showMessages) {
        setCargandoControl(false);
      }
    }
  };

  const handleEnviarControl = async () => {
    try {
      setCargandoControl(true);
      setMensajeControl('');

      console.log('Debug - handleEnviarControl - Valores antes de validar:', {
        producto,
        cliente,
        desde,
        hasta
      });

      // Validar campos requeridos para el control
      if (!producto ||  !desde || !hasta) {
        setMensajeControl('Por favor, complete los campos de producto, cliente, desde y hasta para el control');
        setTipoMensajeControl('error');
        return;
      }

      // Ejecutar control de fardos
      await handleControl(true);

    } catch (error) {
      console.error('Error al ejecutar control:', error);
      setMensajeControl('Error al ejecutar el control. Por favor, intente nuevamente.');
      setTipoMensajeControl('error');
    } finally {
      setCargandoControl(false);
    }
  };

  const handleResumir = () => {
    if (aplicadas.length === 0) {
      setMensajeControl('No hay datos de control para resumir.');
      setTipoMensajeControl('error');
      return;
    }
    // Armar array de fardos seleccionados (checkbox en true Y habilitados)
    const seleccionados = aplicadas.filter(item => {
      const checkboxMarcado = checkboxStates.get(item.id);
      const ordenEnRojo = item.orden !== 'NO';
      const sinStock = (item.stock || 0) === 0;
      const inaccesible = ordenEnRojo || sinStock;
      
      // Solo incluir si está marcado Y no está inaccesible
      return checkboxMarcado && !inaccesible;
    });
    
    // Enriquecer los fardos seleccionados con información adicional
    const fardosEnriquecidos = seleccionados.map(fardo => {
      // Para tipo B (Baja), usar el cliente seleccionado en la sección Seleccionar
      let clienteId = null;
      if (tipoOrden === 'B') {
        // Para baja, usar el cliente seleccionado en la sección Seleccionar
        clienteId = selectedClienteSeleccion ? selectedClienteSeleccion.id : null;
      } else {
        // Para otros tipos, usar el cliente del formulario principal
        clienteId = cliente && cliente !== '' ? parseInt(cliente) : null;
      }
      
      return {
        id_fardo: fardo.id,
        nro_fardo: fardo.nroFardo || fardo.id, // Asegurar que no sea null
        fecha: fardo.fecha,
        origen_id: selectedOrigen ? selectedOrigen.id : (origen && origen !== '' ? parseInt(origen) : null),
        cliente_id: clienteId
      };
    });
    
    // Acumular los fardos enriquecidos al array de fardos acumulados
    setFardosAcumulados(prev => [...prev, ...fardosEnriquecidos]);
    
    setFardosSeleccionados(seleccionados);
    setAplicadas([]); // Limpiar la tabla de Control
    setDesde(''); // Limpiar campo Desde
    setHasta(''); // Limpiar campo Hasta
    const totalFardos = seleccionados.length;
    const aplicados = seleccionados.filter(item => item.aplicado).length;
    const noAplicados = seleccionados.filter(item => !item.aplicado).length;
    const conStock = seleccionados.filter(item => (item.stock || 0) > 0).length;
    const sinStock = seleccionados.filter(item => (item.stock || 0) === 0).length;
    
    // Calcular totales acumulados
    const totalAcumulado = fardosAcumulados.length + seleccionados.length;
    const aplicadosAcumulado = fardosAcumulados.filter(item => item.aplicado).length + aplicados;
    const noAplicadosAcumulado = fardosAcumulados.filter(item => !item.aplicado).length + noAplicados;
    const conStockAcumulado = fardosAcumulados.filter(item => (item.stock || 0) > 0).length + conStock;
    const sinStockAcumulado = fardosAcumulados.filter(item => (item.stock || 0) === 0).length + sinStock;
    
    setResumen({ 
      totalFardos, 
      aplicados, 
      noAplicados, 
      conStock, 
      sinStock,
      totalAcumulado,
      aplicadosAcumulado,
      noAplicadosAcumulado,
      conStockAcumulado,
      sinStockAcumulado
    });
    setMensajeControl('Resumen generado correctamente.');
    setTipoMensajeControl('success');
  };

  if (cargando) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PageWrapper>
      <PageTitle level={2} style={{ marginBottom: '24px' }}>
        Órdenes [SIN CONTRATO]
      </PageTitle>
      
      <StandardCard style={{ marginBottom: '16px' }}>
        <Form form={form} layout="vertical">
          {/* Primera fila - 3 columnas centradas */}
          <Row gutter={[12, 12]} justify="center">
            <Col span={8}>
              <Form.Item 
                label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Tipo de Orden</span>} 
                required
                style={{ marginBottom: '12px' }}
              >
                <Select
                  placeholder="Seleccione tipo de orden"
                  value={tipoOrden}
                  onChange={handleTipoOrdenChange}
                  style={{ width: '100%' }}
                  size="middle"
                >
                  {tiposOrden.map(tipo => (
                    <Option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Producto</span>} 
                required
                style={{ marginBottom: '12px' }}
              >
                <Select
                  placeholder="Seleccione producto"
                  value={producto}
                  onChange={handleProductoChange}
                  style={{ width: '100%' }}
                  size="middle"
                >
                  {productos.map(prod => (
                    <Option key={prod.id} value={prod.id}>
                      {prod.descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Fecha</span>} 
                required
                style={{ marginBottom: '12px' }}
              >
                <DatePicker
                  placeholder="Seleccione fecha"
                  value={fecha}
                  onChange={handleFechaChange}
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  size="middle"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Segunda fila - Cliente para Cambio de Dueño */}
          {tipoOrden === 'D' && (
            <>
              <Row gutter={[12, 12]} style={{ marginBottom: '12px' }}>
                <Col span={24}>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    color: theme.colors.primaryDark, 
                    borderBottom: `2px solid ${theme.colors.primary}`,
                    paddingBottom: '8px',
                    marginBottom: '16px'
                  }}>
                    Cambio de Dueño
                  </div>
                </Col>
              </Row>
              <Row gutter={[12, 12]} style={{ marginBottom: '16px' }}>
                <Col span={12}>
                  <Form.Item 
                    label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Desde Cliente</span>} 
                    required
                    style={{ marginBottom: '12px' }}
                  >
                     <div className="search-dropdown" style={{ position: 'relative', width: '100%' }}>
                       <input
                         type="text"
                         placeholder="Buscar por NC o denominación..."
                         style={{
                           width: '100%',
                           paddingLeft: '28px',
                           paddingRight: selectedClienteCambio ? '28px' : '8px',
                           paddingTop: '5px',
                           paddingBottom: '5px',
                           height: '32px',
                           fontSize: '13px',
                           border: '1px solid #d9d9d9',
                           borderRadius: '4px',
                           backgroundColor: camposPrincipalesBloqueados ? '#f0f0f0' : (selectedClienteCambio ? '#f5f5f5' : 'white'),
                           cursor: camposPrincipalesBloqueados ? 'not-allowed' : 'pointer',
                           opacity: camposPrincipalesBloqueados ? 0.6 : 1
                         }}
                         value={selectedClienteCambio ? `${selectedClienteCambio.nc} - ${selectedClienteCambio.denominacion}` : searchClienteCambio}
                         onChange={camposPrincipalesBloqueados ? undefined : (e => {
                           setSearchClienteCambio(e.target.value);
                           setSelectedClienteCambio(null);
                           setShowSearchClienteCambio(true);
                         })}
                         onFocus={camposPrincipalesBloqueados ? undefined : () => setShowSearchClienteCambio(true)}
                         disabled={camposPrincipalesBloqueados}
                       />
                       {/* Lupa */}
                       <SearchOutlined style={{
                         position: 'absolute',
                         left: '8px',
                         top: '50%',
                         transform: 'translateY(-50%)',
                         color: theme.colors.textSecondary,
                         pointerEvents: 'none',
                         fontSize: '14px'
                       }} />
                       {/* Limpiar selección */}
                       {selectedClienteCambio && !camposPrincipalesBloqueados && (
                         <CloseOutlined
                           style={{
                             position: 'absolute',
                             right: '10px',
                             top: '50%',
                             transform: 'translateY(-50%)',
                             color: theme.colors.textSecondary,
                             cursor: 'pointer',
                             fontSize: '14px',
                             zIndex: 1100
                           }}
                           onClick={() => {
                             setSelectedClienteCambio(null);
                             setSearchClienteCambio('');
                             setShowSearchClienteCambio(true);
                             handleClienteChange('');
                           }}
                           title="Limpiar selección"
                         />
                       )}
                       {/* Dropdown */}
                       {showSearchClienteCambio && clientes.length > 0 && !selectedClienteCambio && !camposPrincipalesBloqueados && (
                         <div style={{
                           position: 'absolute',
                           top: '100%',
                           left: 0,
                           right: 0,
                           backgroundColor: 'white',
                           border: '1px solid #ddd',
                           borderRadius: '4px',
                           maxHeight: '200px',
                           overflowY: 'auto',
                           zIndex: 1000,
                           boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                         }}>
                           {clientes.filter(cli => {
                             const search = (searchClienteCambio || '').trim().toLowerCase();
                             return (
                               (cli.nc || '').toLowerCase().includes(search) ||
                               (cli.denominacion || '').toLowerCase().includes(search)
                             );
                           }).map(cli => (
                             <div
                               key={cli.id}
                               style={{
                                 padding: '8px 12px',
                                 cursor: 'pointer',
                                 borderBottom: '1px solid #eee'
                               }}
                               onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                               onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                               onClick={() => {
                                 setSelectedClienteCambio(cli);
                                 handleClienteChange((cli.id || '').toString());
                                 setShowSearchClienteCambio(false);
                               }}
                             >
                               {cli.nc} - {cli.denominacion}
                             </div>
                           ))}
                         </div>
                       )}
                     </div>
                   </Form.Item>
                 </Col>
                 <Col span={12}>
                   <Form.Item 
                     label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Hasta Cliente</span>} 
                     required
                     style={{ marginBottom: '12px' }}
                   >
                     <div className="search-dropdown" style={{ position: 'relative', width: '100%' }}>
                       <input
                         type="text"
                         placeholder="Buscar por NC o denominación..."
                         style={{
                           width: '100%',
                           paddingLeft: '28px',
                           paddingRight: selectedClienteOtro ? '28px' : '8px',
                           paddingTop: '5px',
                           paddingBottom: '5px',
                           height: '32px',
                           fontSize: '13px',
                           border: '1px solid #d9d9d9',
                           borderRadius: '4px',
                           backgroundColor: selectedClienteOtro ? '#f5f5f5' : 'white',
                           cursor: 'pointer'
                         }}
                         value={selectedClienteOtro ? `${selectedClienteOtro.nc} - ${selectedClienteOtro.denominacion}` : searchClienteOtro}
                         onChange={e => {
                           setSearchClienteOtro(e.target.value);
                           setSelectedClienteOtro(null);
                           setShowSearchClienteOtro(true);
                         }}
                         onFocus={() => setShowSearchClienteOtro(true)}
                       />
                       {/* Lupa */}
                       <SearchOutlined style={{
                         position: 'absolute',
                         left: '8px',
                         top: '50%',
                         transform: 'translateY(-50%)',
                         color: theme.colors.textSecondary,
                         pointerEvents: 'none',
                         fontSize: '14px'
                       }} />
                       {/* Limpiar selección */}
                       {selectedClienteOtro && (
                         <CloseOutlined
                           style={{
                             position: 'absolute',
                             right: '8px',
                             top: '50%',
                             transform: 'translateY(-50%)',
                             color: theme.colors.textSecondary,
                             cursor: 'pointer',
                             fontSize: '14px',
                             zIndex: 1100
                           }}
                           onClick={() => {
                             setSelectedClienteOtro(null);
                             setSearchClienteOtro('');
                             setShowSearchClienteOtro(true);
                           }}
                           title="Limpiar selección"
                         />
                       )}
                       {/* Dropdown */}
                       {showSearchClienteOtro && clientes.length > 0 && !selectedClienteOtro && (
                         <div style={{
                           position: 'absolute',
                           top: '100%',
                           left: 0,
                           right: 0,
                           backgroundColor: 'white',
                           border: '1px solid #ddd',
                           borderRadius: '4px',
                           maxHeight: '200px',
                           overflowY: 'auto',
                           zIndex: 1000,
                           boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                         }}>
                           {clientes.filter(cli => {
                             const search = (searchClienteOtro || '').trim().toLowerCase();
                             return (
                               (cli.nc || '').toLowerCase().includes(search) ||
                               (cli.denominacion || '').toLowerCase().includes(search)
                             );
                           }).map(cli => (
                             <div
                               key={cli.id}
                               style={{
                                 padding: '8px 12px',
                                 cursor: 'pointer',
                                 borderBottom: '1px solid #eee'
                               }}
                               onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                               onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                               onClick={() => {
                                 setSelectedClienteOtro(cli);
                                 setShowSearchClienteOtro(false);
                               }}
                             >
                               {cli.nc} - {cli.denominacion}
                             </div>
                           ))}
                         </div>
                       )}
                     </div>
                   </Form.Item>
                 </Col>
               </Row>
            </>
          )}

          {/* Segunda fila - Subtítulos y campos dinámicos */}
          {tipoOrden === 'A' && (
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: theme.colors.primaryDark, 
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  paddingBottom: '8px',
                  marginBottom: '16px'
                }}>
                  A Otra Sucursal
                </div>
              </Col>
            </Row>
          )}

          {tipoOrden === 'E' && (
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: theme.colors.primaryDark, 
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  paddingBottom: '8px',
                  marginBottom: '16px'
                }}>
                  Retiro Cliente
                </div>
              </Col>
            </Row>
          )}

          {tipoOrden === 'B' && (
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={24}>
                <div style={{ 
                  fontSize: '14px', 
                  fontWeight: 600, 
                  color: theme.colors.primaryDark, 
                  borderBottom: `2px solid ${theme.colors.primary}`,
                  paddingBottom: '8px',
                  marginBottom: '16px'
                }}>
                  Baja
                </div>
              </Col>
            </Row>
          )}

          <Row gutter={[12, 12]} style={{ marginBottom: '12px' }}>
            <Col span={8}>
              {tipoOrden === 'A' && (
                <Form.Item 
                  label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Sucursal</span>} 
                  required
                  style={{ marginBottom: '12px' }}
                >
                  <Select
                    placeholder="Seleccione sucursal"
                    value={sucursalOtra}
                    onChange={handleSucursalOtraChange}
                    style={{ width: '100%' }}
                    size="middle"
                  >
                    {sucursales
                      .filter(suc => {
                        // Para tipo 'A' (Otra sucursal), no mostrar la sucursal actual
                        if (tipoOrden === 'A') {
                          return suc.id !== user?.id_sucursal;
                        }
                        // Para otros tipos, mostrar todas las sucursales
                        return true;
                      })
                      .map(suc => (
                        <Option key={suc.id} value={suc.id}>
                          {suc.denominacion}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
            {tipoOrden === 'A' && (
              <>
                <Col span={8}>
                  <Form.Item 
                    label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Empresa Destino</span>} 
                    required
                    style={{ marginBottom: '12px' }}
                  >
                    <Input
                      value={empresa}
                      onChange={handleEmpresaChange}
                      placeholder="Ingrese empresa destino"
                      size="middle"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item 
                    label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Dirección</span>} 
                    required
                    style={{ marginBottom: '12px' }}
                  >
                    <Input
                      value={direccion}
                      onChange={handleDireccionChange}
                      placeholder="Ingrese dirección"
                      size="middle"
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          {/* Tercera fila - Motivo y Observación para tipo Baja */}
          {tipoOrden === 'B' && (
            <Row gutter={[12, 12]} style={{ marginBottom: '12px' }}>
              <Col span={8}>
                <Form.Item 
                  label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Motivo</span>} 
                  required
                  style={{ marginBottom: '12px' }}
                >
                  <Select
                    placeholder="Seleccione motivo"
                    value={motivo}
                    onChange={handleMotivoChange}
                    style={{ width: '100%' }}
                    size="middle"
                  >
                    {motivos.map(mot => (
                      <Option key={mot.value} value={mot.value}>
                        {mot.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item 
                  label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Observación</span>} 
                  required
                  style={{ marginBottom: '12px' }}
                >
                  <Input
                    value={descripcionMotivo}
                    onChange={handleDescripcionMotivoChange}
                    placeholder="Ingrese observación"
                    size="middle"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {tipoOrden === 'E' && (
            <Row gutter={[12, 12]} style={{ marginBottom: '12px' }}>
              <Col span={8}>
                <Form.Item 
                  label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Destino</span>} 
                  required
                  style={{ marginBottom: '12px' }}
                >
                  <div className="search-dropdown" style={{ position: 'relative', width: '100%' }}>
                    <input
                      type="text"
                      placeholder="Buscar por NC o denominación..."
                      style={{
                        width: '100%',
                        paddingLeft: '28px',
                        paddingRight: selectedDestinoRetiro ? '28px' : '8px',
                        paddingTop: '5px',
                        paddingBottom: '5px',
                        height: '32px',
                        fontSize: '13px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        backgroundColor: camposPrincipalesBloqueados ? '#f0f0f0' : (selectedDestinoRetiro ? '#f5f5f5' : 'white'),
                        cursor: camposPrincipalesBloqueados ? 'not-allowed' : 'pointer',
                        opacity: camposPrincipalesBloqueados ? 0.6 : 1
                      }}
                      value={selectedDestinoRetiro ? `${selectedDestinoRetiro.nc} - ${selectedDestinoRetiro.denominacion}` : searchDestinoRetiro}
                      onChange={camposPrincipalesBloqueados ? undefined : (e => {
                        setSearchDestinoRetiro(e.target.value);
                        setSelectedDestinoRetiro(null);
                        setShowSearchDestinoRetiro(true);
                      })}
                      onFocus={camposPrincipalesBloqueados ? undefined : () => setShowSearchDestinoRetiro(true)}
                      disabled={camposPrincipalesBloqueados}
                    />
                    {/* Lupa */}
                    <SearchOutlined style={{
                      position: 'absolute',
                      left: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: theme.colors.textSecondary,
                      pointerEvents: 'none',
                      fontSize: '14px'
                    }} />
                    {/* Limpiar selección */}
                    {selectedDestinoRetiro && !camposPrincipalesBloqueados && (
                      <CloseOutlined
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: theme.colors.textSecondary,
                          cursor: 'pointer',
                          fontSize: '14px',
                          zIndex: 1100
                        }}
                        onClick={() => {
                          setSelectedDestinoRetiro(null);
                          setSearchDestinoRetiro('');
                          setShowSearchDestinoRetiro(true);
                          handleDestinoChange('');
                        }}
                        title="Limpiar selección"
                      />
                    )}
                    {/* Dropdown */}
                    {showSearchDestinoRetiro && destinos.length > 0 && !selectedDestinoRetiro && !camposPrincipalesBloqueados && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {destinos.filter(dest => {
                          const search = (searchDestinoRetiro || '').trim().toLowerCase();
                          return (
                            (dest.nc || '').toLowerCase().includes(search) ||
                            (dest.denominacion || '').toLowerCase().includes(search)
                          );
                        }).map(dest => (
                          <div
                            key={dest.id}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee'
                            }}
                            onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                            onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                            onClick={() => {
                              setSelectedDestinoRetiro(dest);
                              handleDestinoChange((dest.id || '').toString());
                              setShowSearchDestinoRetiro(false);
                            }}
                          >
                            {dest.nc} - {dest.denominacion}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Empresa" required>
                  <Input
                    value={empresa}
                    onChange={handleEmpresaChange}
                    placeholder="Ingrese empresa"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Dirección" required>
                  <Input
                    value={direccion}
                    onChange={handleDireccionChange}
                    placeholder="Ingrese dirección"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {(tipoOrden === 'E' || tipoOrden === 'A') && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Provincia" required>
                  <Select
                    placeholder="Seleccione provincia"
                    value={provincia}
                    onChange={handleProvinciaChange}
                    style={{ width: '100%' }}
                  >
                    {provincias.map(prov => (
                      <Option key={prov.id} value={prov.id}>
                        {prov.provincia}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Localidad" required>
                  <Select
                    placeholder="Seleccione localidad"
                    value={localidad}
                    onChange={handleLocalidadChange}
                    style={{ width: '100%' }}
                    disabled={!provincia}
                  >
                    {localidades.map(loc => (
                      <Option key={loc.id} value={loc.id}>
                        {loc.localidad}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form>
      </StandardCard>

      {/* Formulario secundario con 3 secciones */}
      <StandardCard style={{ marginTop: '16px' }}>
        <Row gutter={[12, 12]}>
          {/* Selección */}
          <Col span={8}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: 12, 
              backgroundColor: theme.colors.primaryDark, 
              color: theme.colors.textLight, 
              padding: '10px 12px', 
              borderRadius: `${theme.styles.borderRadius} ${theme.styles.borderRadius} 0 0`,
              textAlign: 'center',
              fontSize: '14px'
            }}>
              Selección
            </div>
            {(tipoOrden === 'D' || tipoOrden === 'E') && (
              <Alert
                message={tipoOrden === 'D' ? 
                  'Origen y Cliente se configuran automáticamente desde el Cliente seleccionado' :
                  'Origen y Cliente se configuran automáticamente desde el Destino seleccionado'
                }
                type="info"
                icon={<InfoCircleOutlined />}
                showIcon
                style={{
                  marginBottom: '12px',
                  fontSize: '12px',
                  borderRadius: theme.styles.borderRadius
                }}
              />
            )}
            {tipoOrden === 'B' && (
              <Alert
                message="Para órdenes de Baja, debe seleccionar manualmente el Origen y Cliente"
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                style={{
                  marginBottom: '12px',
                  fontSize: '12px',
                  borderRadius: theme.styles.borderRadius
                }}
              />
            )}
            {tipoOrden === 'B' && (origenBajaBloqueado || clienteBajaBloqueado) && (
              <Alert
                message={`${origenBajaBloqueado && clienteBajaBloqueado ? 'Origen y Cliente bloqueados' : origenBajaBloqueado ? 'Origen bloqueado' : 'Cliente bloqueado'} - Solo se pueden cambiar con Grabar o recarga de página`}
                type="success"
                icon={<CheckCircleOutlined />}
                showIcon
                style={{
                  marginBottom: '12px',
                  fontSize: '12px',
                  borderRadius: theme.styles.borderRadius
                }}
              />
            )}
            {camposPrincipalesBloqueados && (tipoOrden === 'D' || tipoOrden === 'E') && (
              <Alert
                message={tipoOrden === 'D' ? 
                  'Cliente bloqueado - Los datos se han transferido a Seleccionar' :
                  'Destino bloqueado - Los datos se han transferido a Seleccionar'
                }
                type="success"
                icon={<CheckCircleOutlined />}
                showIcon
                style={{
                  marginBottom: '12px',
                  fontSize: '12px',
                  borderRadius: theme.styles.borderRadius
                }}
              />
            )}
            <Form layout="vertical">
              <Form.Item 
                label={<span style={{ fontSize: '13px', fontWeight: 500 }}>Origen</span>}
                style={{ marginBottom: '12px' }}
              >
                <div className="search-dropdown" style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="text"
                    placeholder="Buscar por NC o denominación..."
                    style={{
                      width: '100%',
                      paddingLeft: '28px',
                      paddingRight: selectedOrigen ? '28px' : '8px',
                      paddingTop: '5px',
                      paddingBottom: '5px',
                      height: '32px',
                      fontSize: '13px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      backgroundColor: (selectedOrigen || (tipoOrden === 'D' || tipoOrden === 'E') || (tipoOrden === 'B' && origenBajaBloqueado)) ? '#f5f5f5' : 'white',
                      cursor: (tipoOrden === 'D' || tipoOrden === 'E' || (tipoOrden === 'B' && origenBajaBloqueado)) ? 'not-allowed' : 'pointer',
                      opacity: (tipoOrden === 'D' || tipoOrden === 'E' || (tipoOrden === 'B' && origenBajaBloqueado)) ? 0.7 : 1
                    }}
                    value={selectedOrigen ? `${selectedOrigen.nc} - ${selectedOrigen.denominacion}` : searchOrigen}
                    onChange={e => {
                      if (tipoOrden !== 'D' && tipoOrden !== 'E' && !(tipoOrden === 'B' && origenBajaBloqueado)) {
                        setSearchOrigen(e.target.value);
                        setSelectedOrigen(null);
                        setShowSearchOrigen(true);
                      }
                    }}
                    onFocus={() => {
                      if (tipoOrden !== 'D' && tipoOrden !== 'E' && !(tipoOrden === 'B' && origenBajaBloqueado)) {
                        setShowSearchOrigen(true);
                      }
                    }}
                    disabled={tipoOrden === 'D' || tipoOrden === 'E' || (tipoOrden === 'B' && origenBajaBloqueado)}
                  />
                  {/* Lupa */}
                  <SearchOutlined style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.colors.textSecondary,
                    pointerEvents: 'none',
                    fontSize: '14px'
                  }} />
                                      {/* Limpiar selección */}
                    {selectedOrigen && (tipoOrden !== 'D' && tipoOrden !== 'E') && !(tipoOrden === 'B' && origenBajaBloqueado) && (
                      <CloseOutlined
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: theme.colors.textSecondary,
                          cursor: 'pointer',
                          fontSize: '14px',
                          zIndex: 1100
                        }}
                        onClick={() => {
                          setSelectedOrigen(null);
                          setSearchOrigen('');
                          setShowSearchOrigen(true);
                          setOrigen('');
                          // Para tipo B, desbloquear solo el campo Origen al limpiar
                          if (tipoOrden === 'B') {
                            setOrigenBajaBloqueado(false);
                          }
                        }}
                        title="Limpiar selección"
                      />
                    )}
                                      {/* Dropdown */}
                    {showSearchOrigen && destinos.length > 0 && !selectedOrigen && (tipoOrden !== 'D' && tipoOrden !== 'E') && !(tipoOrden === 'B' && origenBajaBloqueado) && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {destinos.filter(dest => {
                        const search = (searchOrigen || '').trim().toLowerCase();
                        return (
                          (dest.nc || '').toLowerCase().includes(search) ||
                          (dest.denominacion || '').toLowerCase().includes(search)
                        );
                      }).map(dest => (
                        <div
                          key={dest.id}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                          onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                          onClick={() => {
                            setSelectedOrigen(dest);
                            setOrigen((dest.id || '').toString());
                            setShowSearchOrigen(false);
                            
                            // Para tipo B (Baja), bloquear solo el campo Origen después de seleccionarlo
                            if (tipoOrden === 'B') {
                              setOrigenBajaBloqueado(true);
                            }
                          }}
                        >
                          {dest.nc} - {dest.denominacion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
              <Form.Item label="Cliente">
                <div className="search-dropdown" style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="text"
                    placeholder="Buscar por NC o denominación..."
                    style={{
                      width: '100%',
                      paddingLeft: '30px',
                      paddingRight: selectedClienteSeleccion ? '30px' : '12px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px',
                      backgroundColor: (selectedClienteSeleccion || (tipoOrden === 'D' || tipoOrden === 'E') || (tipoOrden === 'B' && clienteBajaBloqueado)) ? '#f5f5f5' : 'white',
                      cursor: (tipoOrden === 'D' || tipoOrden === 'E' || (tipoOrden === 'B' && clienteBajaBloqueado)) ? 'not-allowed' : 'pointer',
                      opacity: (tipoOrden === 'D' || tipoOrden === 'E' || (tipoOrden === 'B' && clienteBajaBloqueado)) ? 0.7 : 1
                    }}
                    value={selectedClienteSeleccion ? `${selectedClienteSeleccion.nc} - ${selectedClienteSeleccion.denominacion}` : searchClienteSeleccion}
                    onChange={e => {
                      if (tipoOrden !== 'D' && tipoOrden !== 'E' && !(tipoOrden === 'B' && clienteBajaBloqueado)) {
                        setSearchClienteSeleccion(e.target.value);
                        setSelectedClienteSeleccion(null);
                        setShowSearchClienteSeleccion(true);
                      }
                    }}
                    onFocus={() => {
                      if (tipoOrden !== 'D' && tipoOrden !== 'E' && !(tipoOrden === 'B' && clienteBajaBloqueado)) {
                        setShowSearchClienteSeleccion(true);
                      }
                    }}
                    disabled={tipoOrden === 'D' || tipoOrden === 'E' || (tipoOrden === 'B' && clienteBajaBloqueado)}
                  />
                  {/* Lupa */}
                  <SearchOutlined style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.colors.textSecondary,
                    pointerEvents: 'none',
                    fontSize: '14px'
                  }} />
                                      {/* Limpiar selección */}
                    {selectedClienteSeleccion && (tipoOrden !== 'D' && tipoOrden !== 'E') && !(tipoOrden === 'B' && clienteBajaBloqueado) && (
                      <CloseOutlined
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: theme.colors.textSecondary,
                          cursor: 'pointer',
                          fontSize: '14px',
                          zIndex: 1100
                        }}
                        onClick={() => {
                          setSelectedClienteSeleccion(null);
                          setSearchClienteSeleccion('');
                          setShowSearchClienteSeleccion(true);
                          setCliente('');
                          // Para tipo B, desbloquear solo el campo Cliente al limpiar
                          if (tipoOrden === 'B') {
                            setClienteBajaBloqueado(false);
                          }
                        }}
                        title="Limpiar selección"
                      />
                    )}
                                      {/* Dropdown */}
                    {showSearchClienteSeleccion && clientes.length > 0 && !selectedClienteSeleccion && (tipoOrden !== 'D' && tipoOrden !== 'E') && !(tipoOrden === 'B' && clienteBajaBloqueado) && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      {clientes.filter(cli => {
                        const search = (searchClienteSeleccion || '').trim().toLowerCase();
                        return (
                          (cli.nc || '').toLowerCase().includes(search) ||
                          (cli.denominacion || '').toLowerCase().includes(search)
                        );
                      }).map(cli => (
                        <div
                          key={cli.id}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseOver={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                          onMouseOut={e => (e.currentTarget.style.backgroundColor = 'white')}
                          onClick={() => {
                            setSelectedClienteSeleccion(cli);
                            setCliente((cli.id || '').toString());
                            setShowSearchClienteSeleccion(false);
                            
                            // Para tipo B (Baja), bloquear solo el campo Cliente después de seleccionarlo
                            if (tipoOrden === 'B') {
                              setClienteBajaBloqueado(true);
                            }
                          }}
                        >
                          {cli.nc} - {cli.denominacion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
              <Form.Item label="Desde">
                <Input
                  type="number"
                  value={desde}
                  onChange={e => setDesde(e.target.value)}
                  placeholder="Número desde"
                />
              </Form.Item>
              <Form.Item label="Hasta">
                <Input
                  type="number"
                  value={hasta}
                  onChange={e => setHasta(e.target.value)}
                  placeholder="Número hasta"
                />
              </Form.Item>
              <Button 
                type="primary" 
                block 
                loading={cargandoControl}
                onClick={handleEnviarControl}
              >
                Enviar
              </Button>
              {tipoOrden === 'B' && (origenBajaBloqueado || clienteBajaBloqueado) && (
                <Button 
                  type="default" 
                  block 
                  style={{ marginTop: 8, height: '32px', fontSize: '13px', fontWeight: 500 }}
                  onClick={() => {
                    setSelectedOrigen(null);
                    setSelectedClienteSeleccion(null);
                    setSearchOrigen('');
                    setSearchClienteSeleccion('');
                    setOrigen('');
                    setCliente('');
                    setOrigenBajaBloqueado(false);
                    setClienteBajaBloqueado(false);
                  }}
                >
                  Limpiar Selección
                </Button>
              )}
              <Row gutter={8} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Button 
                    block 
                    onClick={handleResumir} 
                    disabled={aplicadas.length === 0}
                    size="middle"
                    style={{ 
                      height: '36px', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      backgroundColor: theme.colors.info,
                      borderColor: theme.colors.info,
                      color: theme.colors.textLight,
                      borderRadius: theme.styles.borderRadius,
                      boxShadow: theme.styles.boxShadowLight,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (aplicadas.length > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                    }}
                  >
                    Resumir
                  </Button>
                </Col>
                <Col span={12}>
                  <Button 
                    block 
                    loading={grabarCargando}
                    onClick={handleEnviar}
                    disabled={!resumen || fardosAcumulados.length === 0}
                    size="middle"
                    style={{ 
                      height: '36px', 
                      fontSize: '13px', 
                      fontWeight: 600,
                      backgroundColor: theme.colors.secondary,
                      borderColor: theme.colors.secondary,
                      color: theme.colors.textLight,
                      borderRadius: theme.styles.borderRadius,
                      boxShadow: theme.styles.boxShadowLight,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (resumen && fardosAcumulados.length > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.backgroundColor = theme.colors.secondaryBright;
                        e.currentTarget.style.borderColor = theme.colors.secondaryBright;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                      e.currentTarget.style.backgroundColor = theme.colors.secondary;
                      e.currentTarget.style.borderColor = theme.colors.secondary;
                    }}
                  >
                    Grabar
                  </Button>
                </Col>
              </Row>
              <Row gutter={8} style={{ marginTop: 12 }}>
                <Col span={12}>
                  <Button 
                    block 
                    onClick={handleTildarTodos} 
                    disabled={aplicadas.length === 0}
                    size="middle"
                    style={{
                      height: '36px',
                      fontSize: '13px',
                      fontWeight: 600,
                      backgroundColor: theme.colors.secondary,
                      borderColor: theme.colors.secondary,
                      color: theme.colors.textLight,
                      borderRadius: theme.styles.borderRadius,
                      boxShadow: theme.styles.boxShadowLight,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      if (aplicadas.length > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.backgroundColor = theme.colors.secondaryBright;
                        e.currentTarget.style.borderColor = theme.colors.secondaryBright;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                      e.currentTarget.style.backgroundColor = theme.colors.secondary;
                      e.currentTarget.style.borderColor = theme.colors.secondary;
                    }}
                  >
                    <CheckCircleOutlined />
                    Tildar Todos
                  </Button>
                </Col>
                <Col span={12}>
                  <Button 
                    block 
                    onClick={handleDestildarTodos} 
                    disabled={aplicadas.length === 0}
                    size="middle"
                    style={{
                      height: '36px',
                      fontSize: '13px',
                      fontWeight: 600,
                      backgroundColor: theme.colors.info,
                      borderColor: theme.colors.info,
                      color: theme.colors.textLight,
                      borderRadius: theme.styles.borderRadius,
                      boxShadow: theme.styles.boxShadowLight,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      if (aplicadas.length > 0) {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                        e.currentTarget.style.borderColor = theme.colors.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = theme.styles.boxShadowLight;
                      e.currentTarget.style.backgroundColor = theme.colors.info;
                      e.currentTarget.style.borderColor = theme.colors.info;
                    }}
                  >
                    <CloseCircleOutlined />
                    Destildar Todos
                  </Button>
                </Col>
              </Row>
            </Form>
          </Col>
          {/* Control */}
          <Col span={8}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: 12, 
              backgroundColor: theme.colors.primaryDark, 
              color: theme.colors.textLight, 
              padding: '10px 12px', 
              borderRadius: `${theme.styles.borderRadius} ${theme.styles.borderRadius} 0 0`,
              textAlign: 'center',
              fontSize: '14px'
            }}>
              Control
            </div>
            <Table
              size="middle"
              dataSource={aplicadas}
              rowKey="id"
              pagination={false}
              bordered
              columns={[
                { title: 'NºFardo', dataIndex: 'nroFardo', key: 'nroFardo', width: 100 },
                { title: 'Aplicado', dataIndex: 'aplicado', key: 'aplicado', width: 100, align: 'center' as const, render: (aplicado: boolean) => {
                  const isAplicado = aplicado;
                  return (
                    <span style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isAplicado ? (
                        <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                      ) : (
                        <CloseCircleOutlined style={{ fontSize: '16px', color: '#ff4d4f' }} />
                      )}
                    </span>
                  );
                } },
                { title: 'Stock', dataIndex: 'stock', key: 'stock', width: 100, align: 'center' as const, render: (stock: number) => {
                  const tieneStock = (stock || 0) > 0;
                  return (
                    <span style={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {tieneStock ? (
                        <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                      ) : (
                        <CloseCircleOutlined style={{ fontSize: '16px', color: '#ff4d4f' }} />
                      )}
                    </span>
                  );
                } },
                { title: 'Orden', dataIndex: 'orden', key: 'orden', width: 100, align: 'center' as const, render: (orden: string) => (
                  <Tag color={orden === 'NO' ? 'success' : 'error'} style={{ fontSize: '12px', fontWeight: 600 }}>
                    {orden}
                  </Tag>
                ) },
                { title: 'Alta/Baja', dataIndex: 'aplicado', key: 'alta', width: 100, align: 'center' as const, render: (_: any, record: any) => {
                  const ordenEnRojo = record.orden !== 'NO';
                  const sinStock = (record.stock || 0) === 0;
                  const deshabilitado = ordenEnRojo || sinStock;
                  const checked = deshabilitado ? false : (checkboxStates.get(record.id) || false);
                  
                  return (
                    <Checkbox 
                      checked={checked}
                      disabled={deshabilitado}
                      onChange={e => handleAplicacionChange(record.id, e.target.checked)} 
                    />
                  );
                } },
              ]}
              locale={{ emptyText: 'No hay datos de control disponibles' }}
              scroll={{ y: 300 }}
            />
          </Col>
          {/* Resumen */}
          <Col span={8}>
            <div style={{ 
              fontWeight: 600, 
              marginBottom: 12, 
              backgroundColor: theme.colors.primaryDark, 
              color: theme.colors.textLight, 
              padding: '10px 12px', 
              borderRadius: `${theme.styles.borderRadius} ${theme.styles.borderRadius} 0 0`,
              textAlign: 'center',
              fontSize: '14px'
            }}>
              Resumen
            </div>
            {resumen ? (
              <>
                <div style={{ 
                  fontWeight: 600, 
                  marginBottom: 12, 
                  color: theme.colors.primaryDark,
                  textAlign: 'center',
                  fontSize: '13px'
                }}>
                  TOTAL ACUMULADO
                </div>
                <Table
                  size="middle"
                  dataSource={[
                    { concepto: 'Total Fardos', cantidad: resumen.totalAcumulado },
                    { concepto: 'Aplicados', cantidad: resumen.aplicadosAcumulado },
                    { concepto: 'No Aplicados', cantidad: resumen.noAplicadosAcumulado },
                    { concepto: 'Con Stock', cantidad: resumen.conStockAcumulado },
                    { concepto: 'Sin Stock', cantidad: resumen.sinStockAcumulado },
                  ]}
                  columns={[
                    { title: 'Concepto', dataIndex: 'concepto', key: 'concepto', width: 120 },
                    { 
                      title: 'Cantidad', 
                      dataIndex: 'cantidad', 
                      key: 'cantidad', 
                      align: 'center' as const, 
                      width: 80,
                      render: (cantidad: number, record: any) => {
                        const isPositive = ['Aplicados', 'Con Stock'].includes(record.concepto);
                        const isNegative = ['No Aplicados', 'Sin Stock'].includes(record.concepto);
                        return (
                          <span style={{ 
                            fontWeight: 600,
                            color: isPositive ? theme.colors.secondary : isNegative ? theme.colors.error : theme.colors.textPrimary
                          }}>
                            {cantidad}
                          </span>
                        );
                      }
                    },
                  ]}
                  pagination={false}
                  bordered
                  style={{ backgroundColor: theme.colors.background }}
                />
                <Button 
                  type="default" 
                  size="middle" 
                  block 
                  onClick={handleLimpiarAcumulados}
                  style={{ 
                    marginTop: 12, 
                    height: '36px', 
                    fontSize: '13px', 
                    fontWeight: 600,
                    borderRadius: theme.styles.borderRadius,
                    borderColor: theme.colors.border,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.primary;
                    e.currentTarget.style.color = theme.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.border;
                    e.currentTarget.style.color = theme.colors.textPrimary;
                  }}
                >
                  Limpiar Acumulados
                </Button>
              </>
            ) : (
              <p className="text-center">No hay datos de resumen disponibles</p>
            )}
            {mensajeControl && (
              <Alert
                message={mensajeControl}
                type={tipoMensajeControl === 'success' ? 'success' : 'error'}
                showIcon
                style={{ 
                  marginTop: 12,
                  borderRadius: theme.styles.borderRadius,
                  fontSize: '12px'
                }}
              />
            )}
          </Col>
        </Row>
      </StandardCard>
    </PageWrapper>
  );
};

export default AltaSinContratos;
